import React from "react";
import { useState, useMemo, useEffect } from "react";
import { fetchPurposeApi } from "../api/PurposeType";
import toast from "react-hot-toast";

import {
  createProjectApi,
  getProjectByIdApi,
  updateProjectApi,
} from "../api/Project";
import { useNavigate, useLocation } from "react-router-dom";
import Editor from "../../common/components/Editor";

type ProjectPurpose = {
  id: string;
  Id: string;
  type: string;
  purpose: string;
  value: string;
  parentId: string | null;
};

type Project = {
  id: string;
  parentId: string | null;
  name: string;
  isHiddenForUser: boolean;
};

interface ProjectFormProps {
  url: string;
}

export const ProjectForm = ({ url }: ProjectFormProps) => {
  const [name, setName] = useState("");
  const [sequence, setSequence] = useState<string | number>("");
  const [purpose, setPurpose] = useState<string | null>(null);
  const [dataPurpose, setDataPurpose] = useState<Project[]>([]);
  const [image, setImage] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [desc, setDesc] = useState("");
  const [details, setDetails] = useState("");
  const [status, setStatus] = useState("");

  const [budget, setBudget] = useState<string | number>("");
  const [adjustment, setAdjustment] = useState<number>(0);
  const [priority, setPriority] = useState<number>(0);

  const [isSubmitting, setIsSubmitting] = useState(false);

  const [editMode, setEditMode] = useState(false);
  const [loading, setLoading] = useState(true);
  const [, setpId] = useState<string>("");

  const navigate = useNavigate();
  const location = useLocation();
  const { id } = location.state || {};

  const placeholder = "";
  const config: Record<string, any> = useMemo(
    () => ({
      readonly: false,
      placeholder: placeholder || "",
    }),
    [placeholder]
  );


  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await fetchPurposeApi(url as string);
        setDataPurpose(res as Project[]);
      } catch (error) {
        console.error("Error fetching Data:", error);
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    const fetchProjectDetails = async () => {
      if (id) {
        setEditMode(true);
        try {
          const response: any = await getProjectByIdApi(id, url);
          if (response) {
            setName(response.name || "");
            setSequence(response.sequence || "");
            setPurpose(response.donationPurposeId || null);
            setDesc(response.description || "");
            setDetails(response.details || "");
            setStatus(response.status || "");
            setBudget(response.budget || "");
            setAdjustment(response.adjustment);
            setPriority(response.priority);
            setImagePreview(response.image || null);

            setLoading(false);
          }
        } catch (error) {
          console.error("Error fetching project details:", error);
        }
      }
    };
    fetchProjectDetails();
  }, [id]);

  useEffect(() => {
    if (id) {
      setpId(id);
      setEditMode(true);
      setLoading(true);
    } else {
      setEditMode(false);

      setLoading(false);
    }
  }, [id]);

  const filteredPurpose = dataPurpose.filter((item) => item.parentId === null);

  const handleImageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setImage(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (isSubmitting) return;
    setIsSubmitting(true);

    if (
      !name ||
      !sequence ||
      !purpose ||
      !desc ||
      !details ||
      !status ||
      !budget ||
      !priority
    ) {
      toast.error("Please fill out all fields");
      setIsSubmitting(false);
      return;
    }

    let imageUrl: File | null = null;
    if (image) {
      imageUrl = image;
    }

    try {
      if (editMode) {
        const existingProject: any = await getProjectByIdApi(id, url);

        await updateProjectApi(
          url,
          id,
          name,
          Number(sequence),
          purpose,
          imageUrl ? imageUrl : existingProject.image,
          desc,
          details,
          status,
          Number(budget),
          Number(adjustment),
          priority
        );
      } else {
        await createProjectApi(
          url,
          name,
          Number(sequence),
          purpose,
          imageUrl,
          desc,
          details,
          status,
          Number(budget),
          Number(adjustment),
          priority
        );
      }

      toast.success(
        editMode
          ? "Project Updated Successfully."
          : "Project Added Successfully."
      );
      handleResetForm();
    } catch (error) {
      console.error("Error submitting project:", error);
      toast.error("An error occurred while submitting the project");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResetForm = () => {
    setEditMode(false);
    setName("");
    setSequence("");
    setPurpose(null);
    setImage(null);
    setDesc("");
    setDetails("");
    setStatus("");
    setBudget("");
    setAdjustment(0);
    setPriority(0);
    navigate(-1);
  };

  return (
    <div className="max-w-6xl p-6">
      <div>
        {loading ? (
          <div className="flex justify-center items-center h-screen">
            <div
              className="flex justify-center items-center h-12 w-12 animate-spin rounded-full border-4 border-solid border-current border-e-transparent align-[-0.125em] text-surface motion-reduce:animate-[spin_1.5s_linear_infinite] text-blue-600"
              role="status"
            >
              <span className="!absolute !-m-px !h-px !w-px !overflow-hidden !whitespace-nowrap !border-0 !p-0 ![clip:rect(0,0,0,0)]">
                Loading...
              </span>
            </div>
          </div>
        ) : (
          <>
            <div className="px-2 md:px-4 mt-4">
              <div className=" text-3xl font-bold text-blue-600 mb-5 mt-5 justify-center">
                {editMode ? "Update Project" : "Add Project"}
              </div>
              <form className="mt-8 space-y-4">
                <div className="flex flex-col gap-4">
                  <div>
                    <label
                      htmlFor="projectName"
                      className="block text-sm font-medium text-black dark:text-gray-200 mb-2"
                    >
                      Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      id="projectName"
                      type="text"
                      required
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="Enter Name"
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="projectName"
                      className="block text-sm font-medium text-black dark:text-gray-200 mb-2"
                    >
                      Sequnece <span className="text-red-500">*</span>
                    </label>
                    <input
                      id="projectName"
                      type="number"
                      required
                      value={sequence}
                      onChange={(e) => setSequence(e.target.value)}
                      placeholder="Enter Sequence"
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div className="flex flex-col space-y-2 ">
                    <label className="font-medium text-black dark:text-gray-200">
                      Purpose <span className="text-red-500">*</span>
                    </label>
                    <select
                      className="p-2 border border-gray-300 rounded-lg text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-500"
                      required
                      value={purpose || ""}
                      onChange={(e) => setPurpose(e.target.value)}
                    >
                      <option value="" disabled>
                        Select Purpose
                      </option>
                      {filteredPurpose.map((item) => (
                        <option key={item.id} value={item.id}>
                          {item.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="flex flex-col space-y-2">
                    <label className="font-medium text-black dark:text-gray-200">
                      Description <span className="text-red-500">*</span>
                    </label>
                    <Editor
                      content={desc}
                      setContent={setDesc}
                      config={config}
                    />
                  </div>
                  <div className="flex flex-col space-y-2 ">
                    <label className="font-medium text-black dark:text-gray-200">
                      Details
                    </label>
                    <Editor
                      content={details}
                      setContent={setDetails}
                      config={config}
                    />
                  </div>

                  <div className="flex flex-col space-y-2 ">
                    <label className="font-medium text-black dark:text-gray-200">
                      Status <span className="text-red-500">*</span>
                    </label>
                    <select
                      className="p-2 border border-gray-300 rounded-lg text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-500"
                      required
                      value={status || ""}
                      onChange={(e) => setStatus(e.target.value)}
                    >
                      <option value="" disabled>
                        Select Status
                      </option>
                      <option value="New">New</option>
                      <option value="Running">Running</option>
                    </select>
                  </div>
                  <div>
                    <label
                      htmlFor="projectName"
                      className="block text-sm font-medium text-black dark:text-gray-200 mb-2"
                    >
                      Budget <span className="text-red-500">*</span>
                    </label>
                    <input
                      id="projectName"
                      type="number"
                      required
                      value={budget}
                      onChange={(e) => setBudget(e.target.value)}
                      placeholder="Enter Budget"
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="projectName"
                      className="block text-sm font-medium text-black dark:text-gray-200 mb-2"
                    >
                      Adjustment
                    </label>
                    <input
                      id="projectName"
                      type="number"
                      required
                      value={adjustment}
                      onChange={(e) => setAdjustment(Number(e.target.value))}
                      placeholder="Enter Donated Amount"
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="projectName"
                      className="block text-sm font-medium text-black dark:text-gray-200 mb-2"
                    >
                      Priority <span className="text-red-500">*</span>
                    </label>
                    <input
                      id="projectName"
                      type="number"
                      required
                      value={priority}
                      onChange={(e) => setPriority(Number(e.target.value))}
                      placeholder="Enter Priority"
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div className="flex flex-col space-y-2">
                    <label className="font-medium text-black dark:text-gray-200">
                      Upload Image
                    </label>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageChange}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    {imagePreview && (
                      <div className="mt-4">
                        <p className="text-sm text-gray-500">Preview:</p>
                        <img
                          src={imagePreview || ""}
                          alt="Preview"
                          className="w-72 h-72 object-cover rounded-md border border-gray-300"
                        />
                      </div>
                    )}
                  </div>
                </div>
              </form>
            </div>
            <div className="flex justify-start mt-4">
              <button
                type="submit"
                className="bg-blue-500 text-white py-2 px-4 rounded-md hover:bg-blue-600 mr-4"
                onClick={handleSubmit}
                disabled={isSubmitting}
              >
                {editMode ? "Update" : "Save and Close"}
              </button>
              <button
                type="button"
                className="bg-blue-500 hover:bg-blue-600 text-white py-2 px-4 rounded-md"
                onClick={handleResetForm}
              >
                Cancel
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};
